import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { BrandMark, Icon, type IconName } from './icons';
import { cx, ThemeSwitch, useOnline } from './ui';
import { ThemeSettings } from './ThemeControls';
import { hasKey, LANGUAGES, useLanguage, useT, type MessageKey } from '../i18n';

/**
 * Navigation.
 *
 * Entries are filtered by permission rather than hidden on page load, so a cashier never
 * sees a menu item that would refuse them. The server still enforces every one of them:
 * this is about not wasting the cashier's time, not about security.
 *
 * On wide screens the menu is a slim rail: an icon over a short label, so the till gets the
 * width back for the bill. Below 1024px it collapses behind a top bar and opens as a full drawer
 * (one <nav> in the DOM either way, so there is one set of links).
 */

interface Entry {
  to: string;
  label: MessageKey;
  permission?: string;
  /** Screens that are useful to more than one job (Admin holds four gated tabs). */
  anyPermission?: string[];
  /** Business module the screen belongs to (packages/core/src/modules.ts); hidden when it is off. */
  module?: string;
  icon: IconName;
}

/**
 * The whole application, in the order a store actually uses it: ring up a sale, keep the
 * catalogue and the stock right, buy more, look after the customer, and only then the
 * back-office screens most people never open.
 */
const ENTRIES: Entry[] = [
  { to: '/', label: 'nav.till', permission: 'pos.sale.create', icon: 'till' },
  { to: '/products', label: 'nav.products', permission: 'catalog.view', icon: 'box' },
  { to: '/inventory', label: 'nav.inventory', permission: 'inventory.view', icon: 'stock' },
  { to: '/purchasing', label: 'nav.purchasing', permission: 'purchase.view', module: 'purchasing', icon: 'truck' },
  { to: '/customers', label: 'nav.customers', permission: 'customer.view', icon: 'users' },
  { to: '/collections', label: 'nav.collections', anyPermission: ['credit.collect', 'credit.view'], module: 'credit', icon: 'receipt' },
  { to: '/returns', label: 'nav.returns', permission: 'pos.return.withInvoice', icon: 'return' },
  { to: '/import', label: 'nav.import', permission: 'catalog.import', module: 'csvImport', icon: 'upload' },
  { to: '/reports', label: 'nav.reports', permission: 'report.view', icon: 'chart' },
  {
    to: '/admin',
    label: 'nav.admin',
    anyPermission: ['admin.users', 'audit.view', 'admin.settings', 'admin.sync'],
    icon: 'settings',
  },
  { to: '/readiness', label: 'nav.readiness', permission: 'admin.readiness', icon: 'pulse' },
  // The platform operator's screen: business type and modules. No store role holds platform.manage.
  { to: '/platform', label: 'nav.platform', permission: 'platform.manage', icon: 'store' },
];

function allowed(entry: Entry, permissions: Set<string>, hasModule: (code: string) => boolean): boolean {
  if (entry.module && !hasModule(entry.module)) return false;
  if (entry.permission) return permissions.has(entry.permission);
  if (entry.anyPermission) return entry.anyPermission.some((permission) => permissions.has(permission));
  return true;
}

/**
 * Guards a back-office route with the same rule as its menu entry, so a typed or bookmarked
 * URL shows one clear "no access" panel instead of a half-loaded page full of refusals.
 */
export function RequireAccess({ path, children }: { path: string; children: React.ReactNode }) {
  const permissions = useAuth((state) => state.permissions);
  // Subscribing to the profile re-renders the guard when the superadmin switches a module.
  useAuth((state) => state.businessProfile);
  const hasModule = useAuth((state) => state.hasModule);
  const t = useT();
  const entry = ENTRIES.find((candidate) => candidate.to === path);
  if (!entry || allowed(entry, permissions, hasModule)) return <>{children}</>;
  const home = ENTRIES.find((candidate) => allowed(candidate, permissions, hasModule));
  // A switched-off module is not a missing permission: say which it is.
  const moduleOff = Boolean(entry.module && !hasModule(entry.module));
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="panel max-w-md p-6 text-center" data-testid="route-no-access">
        <p className="text-lg font-semibold text-slate-900">
          {t(moduleOff ? 'shell.moduleOffTitle' : 'shell.noAccessTitle', { page: t(entry.label) })}
        </p>
        <p className="mt-1 text-sm text-slate-600">{t(moduleOff ? 'shell.moduleOffBody' : 'shell.noAccessBody')}</p>
        {home ? (
          <NavLink to={home.to} className="btn btn-primary mt-4">
            {t('shell.noAccessBack', { page: t(home.label) })}
          </NavLink>
        ) : null}
      </div>
    </div>
  );
}

/** "Sunita Cashier" becomes "SC": a quick visual cue for whose shift this is. */
function initialsOf(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function OnlineDot({ className }: { className?: string }) {
  const online = useOnline();
  const t = useT();
  const tone = online ? 'var(--ui-success-solid)' : 'var(--ui-danger-solid)';
  return (
    <span
      className={cx('inline-flex max-w-full min-w-0 items-center gap-1.5 text-xs font-semibold', className)}
      role="status"
      aria-live="polite"
      data-testid="online-indicator"
    >
      <span
        aria-hidden
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ background: tone, boxShadow: `0 0 0 3px color-mix(in srgb, ${tone} 25%, transparent)` }}
      />
      <span className="min-w-0 [overflow-wrap:anywhere]">{online ? t('shell.online') : t('shell.offline')}</span>
    </span>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { principal, store, signOut, permissions, hasModule } = useAuth();
  const t = useT();
  const roleKey = `role.${(principal?.role ?? '').toLowerCase()}`;
  const roleName = hasKey(roleKey) ? t(roleKey) : (principal?.role ?? '').replace(/_/g, ' ').toLowerCase();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  /** Short desktop screens fold the rail's footer into one account button; this is its panel. */
  const [accountOpen, setAccountOpen] = useState(false);
  const online = useOnline();

  const visible = ENTRIES.filter((entry) => allowed(entry, permissions, hasModule));
  const current = visible.find((entry) => (entry.to === '/' ? location.pathname === '/' : location.pathname.startsWith(entry.to)));

  // A drawer that stays open after navigating hides the page the cashier just asked for.
  useEffect(() => {
    setOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!accountOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setAccountOpen(false);
    };
    const onPointer = (event: PointerEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-account-menu]')) setAccountOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, [accountOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[80] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:shadow-lg"
      >
        {t('shell.skip')}
      </a>

      <header className="no-print flex shrink-0 items-center gap-3 bg-ink px-3 py-2 text-on-ink lg:hidden">
        <button
          type="button"
          className="btn btn-touch border-on-ink/20 bg-on-ink/5 px-3 text-on-ink shadow-none hover:bg-on-ink/10 active:bg-on-ink/15"
          aria-label={open ? t('shell.closeMenu') : t('shell.openMenu')}
          aria-expanded={open}
          aria-controls="app-nav"
          onClick={() => setOpen((value) => !value)}
        >
          <Icon name={open ? 'close' : 'menu'} size={20} strokeWidth={2} />
        </button>
        <BrandMark size={32} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm leading-tight font-bold">{store?.name ?? 'Supermarket POS'}</p>
          <p className="truncate text-[11px] leading-tight text-on-ink-2">{current ? t(current.label) : ''}</p>
        </div>
        <OnlineDot className="text-on-ink-2" />
      </header>

      {open ? (
        <div className="fixed inset-0 z-30 bg-black/55 lg:hidden" aria-hidden onClick={() => setOpen(false)} />
      ) : null}

      <nav
        id="app-nav"
        aria-label={t('shell.menu')}
        className={cx(
          'no-print w-64 shrink-0 flex-col bg-ink text-on-ink-2 lg:static lg:flex lg:w-[5.25rem]',
          open ? 'fixed inset-y-0 left-0 z-40 flex shadow-2xl' : 'hidden',
        )}
      >
        <div
          className="flex items-center gap-3 border-b border-on-ink/10 px-4 py-4 lg:justify-center lg:px-2 tight:py-2"
          title={store?.name ?? undefined}
        >
          <BrandMark size={36} />
          <div className="min-w-0 lg:hidden">
            <p className="line-clamp-2 text-sm leading-tight font-bold text-on-ink">{store?.name ?? 'Supermarket POS'}</p>
            <p className="mt-0.5 truncate text-[11px] leading-tight text-on-ink-3">{store?.gstin ?? store?.code ?? ''}</p>
          </div>
        </div>

        <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-3 lg:space-y-1 lg:px-1.5 tight:space-y-0 tight:py-1.5">
          {visible.map((entry) => (
            <li key={entry.to}>
              <NavLink
                to={entry.to}
                end={entry.to === '/'}
                title={t(entry.label)}
                data-testid={`nav-${entry.to === '/' ? 'till' : entry.to.replace(/^\//, '')}`}
                className={({ isActive }) =>
                  cx(
                    'group flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm transition-colors',
                    'lg:min-h-0 lg:flex-col lg:justify-center lg:gap-1 lg:rounded-xl lg:px-1 lg:py-2 lg:text-center lg:text-[10.5px] lg:leading-tight',
                    // Short laptop screens: an admin's eleven entries must fit without scrolling. The
                    // superadmin's twelve (Platform) get their own tighter set: two competing padding
                    // classes do not reliably override each other, so the crowded case replaces them.
                    visible.length > 11
                      ? 'tight:gap-0 tight:py-[3px] tight:[&>svg]:size-4'
                      : 'tight:gap-0.5 tight:py-1 tight:[&>svg]:size-[18px]',
                    isActive
                      ? 'bg-on-ink/12 font-semibold text-on-ink shadow-[inset_3px_0_0_var(--color-till-500)] lg:shadow-none'
                      : 'font-medium hover:bg-on-ink/6 hover:text-on-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      name={entry.icon}
                      size={21}
                      className={cx('shrink-0 transition-colors', isActive ? 'text-sky-300' : 'text-on-ink-3 group-hover:text-on-ink-2')}
                    />
                    <span className="min-w-0 [overflow-wrap:anywhere] lg:w-full">{t(entry.label)}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="relative border-t border-on-ink/10 tight:px-1.5 tight:py-2" data-account-menu>
          {/* Short desktop screens: the footer below folds into this one button and opens beside the rail. */}
          <button
            type="button"
            className="relative mx-auto hidden h-10 w-10 items-center justify-center rounded-full bg-on-ink/10 text-xs font-bold tracking-wide text-on-ink hover:bg-on-ink/20 tight:flex"
            aria-expanded={accountOpen}
            aria-label={t('shell.account', { name: principal?.fullName ?? '', status: online ? t('shell.online') : t('shell.offline') })}
            title={principal?.fullName}
            onClick={() => setAccountOpen((value) => !value)}
          >
            {initialsOf(principal?.fullName)}
            <span
              aria-hidden
              className="absolute right-0 bottom-0 h-3 w-3 rounded-full border-2 border-ink"
              style={{ background: online ? 'var(--ui-success-solid)' : 'var(--ui-danger-solid)' }}
            />
          </button>
          <div
            className={cx(
              'space-y-3 px-4 py-3 lg:space-y-2.5 lg:px-1.5',
              'tight:absolute tight:bottom-2 tight:left-full tight:z-50 tight:ml-3 tight:w-60 tight:rounded-xl tight:bg-ink-2 tight:p-3 tight:shadow-2xl',
              accountOpen ? null : 'tight:hidden',
            )}
          >
            <div className="flex items-center justify-between gap-2 lg:flex-col tight:flex-row">
              <OnlineDot className="text-on-ink-2 lg:text-[10.5px]" />
              <ThemeSwitch className="inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-on-ink-2 hover:bg-on-ink/10 hover:text-on-ink lg:h-9 lg:w-9 lg:justify-center lg:px-0 lg:[&>span]:hidden tight:w-auto tight:px-2 tight:[&>span]:inline" />
            </div>
            {/* The desktop rail is too narrow for the theme buttons, so there they open as a panel
                beside the rail instead of spilling below the bottom of the screen. */}
            <details className="rounded-md px-2 text-xs text-on-ink-2 lg:relative lg:px-0 lg:text-center tight:text-left">
              <summary className="cursor-pointer py-1 font-medium hover:text-on-ink">{t('shell.display')}</summary>
              <ThemeSettings className="pb-2 lg:absolute lg:bottom-0 lg:left-full lg:z-50 lg:ml-3 lg:w-64 lg:rounded-xl lg:border lg:border-slate-200 lg:bg-white lg:p-3 lg:text-left lg:text-slate-900 lg:shadow-2xl" />
            </details>
            <ShellLanguageSwitch />
            <div className="flex items-center gap-2.5 lg:justify-center tight:justify-start" title={principal?.fullName}>
              <span
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-on-ink/10 text-xs font-bold tracking-wide text-on-ink"
              >
                {initialsOf(principal?.fullName)}
              </span>
              <div className="min-w-0 lg:sr-only tight:not-sr-only">
                <p className="truncate text-sm leading-tight font-semibold text-on-ink">{principal?.fullName}</p>
                <p className="truncate text-[11px] leading-tight text-on-ink-3 capitalize">
                  {roleName}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border border-on-ink/15 px-2 text-xs font-semibold text-on-ink-2 hover:bg-on-ink/10 hover:text-on-ink"
              title={t('shell.signOut')}
              onClick={() => {
                signOut();
                navigate('/');
              }}
            >
              <Icon name="logout" size={16} />
              <span className="lg:sr-only tight:not-sr-only">{t('shell.signOut')}</span>
            </button>
          </div>
        </div>
      </nav>

      <main id="main" tabIndex={-1} className="min-h-0 min-w-0 flex-1 overflow-hidden bg-slate-100 outline-none">
        {children}
      </main>
    </div>
  );
}

/** The language for every screen, from the LANGUAGES registry: one button per language. */
function ShellLanguageSwitch() {
  const t = useT();
  const language = useLanguage((state) => state.language);
  const setLanguage = useLanguage((state) => state.setLanguage);
  return (
    <div className="flex gap-1 px-2 lg:justify-center lg:px-0 tight:justify-start" role="group" aria-label={t('shell.language')} data-testid="shell-language">
      {LANGUAGES.map((entry) => (
        <button
          key={entry.code}
          type="button"
          lang={entry.code}
          aria-pressed={language === entry.code}
          aria-label={entry.label}
          title={entry.label}
          className={cx(
            'min-h-8 min-w-8 rounded-md px-1.5 text-xs font-semibold',
            language === entry.code ? 'bg-on-ink/15 text-on-ink' : 'text-on-ink-3 hover:bg-on-ink/10 hover:text-on-ink',
          )}
          style={'font' in entry ? { fontFamily: entry.font } : undefined}
          onClick={() => setLanguage(entry.code)}
        >
          {entry.short}
        </button>
      ))}
    </div>
  );
}

/** Page header used by every back-office screen: title, optional subtitle, actions on the right. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2.5 border-b border-slate-200 bg-white px-4 py-3 sm:px-5 sm:py-4">
      <div className="min-w-0">
        <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-title">{title}</h1>
        {subtitle ? <p className="text-[13px] text-slate-500 sm:text-sm">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 max-sm:w-full">{actions}</div> : null}
    </div>
  );
}
